import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  TextField,
  Alert,
  Snackbar,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Preview as PreviewIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { EmailTemplate, EmailVariables, Candidate } from '../../types';
import emailService from '../../services/emailService';

interface EmailExportDialogProps {
  open: boolean;
  onClose: () => void;
  template: EmailTemplate | null;
  candidate: Candidate | null;
  customVariables?: Partial<EmailVariables>;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-tabpanel-${index}`}
      aria-labelledby={`email-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const EmailExportDialog: React.FC<EmailExportDialogProps> = ({
  open,
  onClose,
  template,
  candidate,
  customVariables = {}
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [processedEmail, setProcessedEmail] = useState<{
    subject: string;
    htmlBody: string;
    textBody: string;
  } | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  useEffect(() => {
    if (template && candidate && open) {
      generateEmailContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, candidate, customVariables, open]);

  const generateEmailContent = useCallback(async () => {
    if (!template || !candidate) return;

    try {
      // Generate variables from candidate data
      const baseVariables: EmailVariables = {
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        email: candidate.email,
        phone: candidate.phone,
        accessPasscode: candidate.accessPasscode,
        portalUrl: `https://m4tracker.com/${(candidate.firstName + candidate.lastName)
          .toLowerCase()
          .replace(/[^a-z0-9]/g, '')}`
      };

      // Merge with custom variables
      const allVariables = { ...baseVariables, ...customVariables };

      // Process the template
      const processed = await emailService.processEmailTemplate(template, allVariables);

      setProcessedEmail({
        subject: processed.subject,
        htmlBody: processed.body,
        textBody: stripHtmlTags(processed.body)
      });
    } catch (error) {
      console.error('Error processing email template:', error);
    }
  }, [template, candidate, customVariables]);

  const stripHtmlTags = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || "";
  };

  const copyToClipboard = async (content: string, type: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopySuccess(`${type} copied to clipboard!`);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setCopySuccess(`Failed to copy ${type.toLowerCase()}`);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatSubjectForCopy = () => {
    if (!processedEmail) return '';
    return processedEmail.subject;
  };

  const formatHtmlForCopy = () => {
    if (!processedEmail) return '';
    return processedEmail.htmlBody;
  };

  const formatTextForCopy = () => {
    if (!processedEmail) return '';
    return processedEmail.textBody;
  };

  if (!template || !candidate) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <EmailIcon color="primary" />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              Email Export - {template.templateName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ready to copy and paste into Outlook, Gmail, or any email client
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <Divider />

        {/* Candidate Info */}
        <Box sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" gutterBottom>
            Recipient Information:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={`${candidate.firstName} ${candidate.lastName}`}
              color="primary"
              variant="outlined"
            />
            <Chip
              size="small"
              label={candidate.email}
              color="secondary"
              variant="outlined"
            />
            {candidate.accessPasscode && (
              <Chip
                size="small"
                label={`Access: ${candidate.accessPasscode}`}
                color="success"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        <Divider />

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab
              icon={<EmailIcon />}
              label="Subject Line"
              iconPosition="start"
            />
            <Tab
              icon={<PreviewIcon />}
              label="HTML Body (Visual)"
              iconPosition="start"
            />
            <Tab
              icon={<CodeIcon />}
              label="HTML Code"
              iconPosition="start"
            />
          </Tabs>
        </Box>

        <DialogContent sx={{ p: 0, flexGrow: 1 }}>
          {/* Subject Tab */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                📧 Subject Line
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Copy this subject line for your email:
              </Typography>
            </Box>

            <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
              <TextField
                fullWidth
                multiline
                value={formatSubjectForCopy()}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.9rem' }
                }}
              />
            </Paper>

            <Button
              variant="contained"
              startIcon={<CopyIcon />}
              onClick={() => copyToClipboard(formatSubjectForCopy(), 'Subject line')}
              fullWidth
              size="large"
            >
              Copy Subject Line to Clipboard
            </Button>
          </TabPanel>

          {/* HTML Preview Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                👀 Email Preview (How it will look)
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                This shows how the email will appear when sent:
              </Typography>
            </Box>

            <Paper
              sx={{
                p: 2,
                maxHeight: '400px',
                overflow: 'auto',
                border: '2px dashed',
                borderColor: 'primary.light',
                mb: 2
              }}
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: processedEmail?.htmlBody || ''
                }}
                style={{
                  fontFamily: 'Arial, sans-serif',
                  lineHeight: '1.5'
                }}
              />
            </Paper>

            <Button
              variant="contained"
              startIcon={<CopyIcon />}
              onClick={() => copyToClipboard(formatHtmlForCopy(), 'HTML email body')}
              fullWidth
              size="large"
            >
              Copy HTML Email Body to Clipboard
            </Button>
          </TabPanel>

          {/* HTML Code Tab */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                🔧 HTML Source Code
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Raw HTML code - perfect for pasting into email clients that support HTML:
              </Typography>
            </Box>

            <Paper sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={12}
                value={formatHtmlForCopy()}
                variant="outlined"
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: 'Courier New, monospace',
                    fontSize: '0.8rem'
                  }
                }}
              />
            </Paper>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(formatHtmlForCopy(), 'HTML code')}
                fullWidth
                size="large"
              >
                Copy HTML Code
              </Button>
              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(formatTextForCopy(), 'Plain text version')}
                fullWidth
                size="large"
              >
                Copy Plain Text Version
              </Button>
            </Box>
          </TabPanel>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2, bgcolor: 'background.default' }}>
          <Alert severity="info" sx={{ flexGrow: 1, mr: 2 }}>
            <strong>How to use:</strong> Copy the content above and paste it into your email client.
            Outlook, Gmail, and most modern email clients support HTML formatting.
          </Alert>
          <Button onClick={onClose} size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySuccess(null)}
          severity="success"
          variant="filled"
        >
          {copySuccess}
        </Alert>
      </Snackbar>
    </>
  );
};